// The instructor console's roster (EDU-6, ARCH §29.8) as data. Pure and DOM-free, so the
// row building, the "raised hands first" order and the `m:ss` clock are unit-testable;
// `ui/screens/classroom.ts` renders these rows and nothing else. Everything here is
// chrome: it reads the server's `Players` roster and the `LessonUpdate` rows sent to
// instructors, and never enters the tick (D-TF-151).
import { TICK } from '../../sim/config';

/** One connected player as the `Players` roster reports them (v10). */
export interface RosterPlayer {
  readonly id: number;
  readonly name: string;
  readonly role?: string;
  readonly hand?: number;
}

/** A student's result row as the instructor last received it. */
export interface RosterResult {
  readonly objectives: Readonly<Record<string, number | null>>;
  readonly quiz?: { readonly best: number; readonly n: number };
  readonly ticksOnTask: number;
}

/** One roster line (§29.8): name, ✋ + queue number, objectives `k / n`, quiz best,
 *  ticks-on-task. `total` is 0 until the student's first row arrives. */
export interface RosterRow {
  id: number;
  name: string;
  role: string;
  hand?: number;
  done: number;
  total: number;
  quizBest?: number;
  ticksOnTask: number;
}

/** Roles that run the room rather than sit in it — never a roster row. */
const STAFF = new Set(['owner', 'instructor']);

/** Ticks as `m:ss` at the sim's tick rate (§29.8). */
export function formatTicks(ticks: number): string {
  const secs = Math.max(0, Math.floor(ticks / TICK.TPS));
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

/** `k / n` — objectives completed out of objectives declared. */
export function formatProgress(row: Pick<RosterRow, 'done' | 'total'>): string {
  return `${row.done} / ${row.total}`;
}

/** Every connected student (everyone but `selfId` and the room's staff), raised hands
 *  first in queue order, then by name. */
export function buildRoster(
  players: readonly RosterPlayer[], results: ReadonlyMap<number, { result: RosterResult }>, selfId: number,
): RosterRow[] {
  const rows: RosterRow[] = [];
  for (const p of players) {
    if (p.id === selfId || STAFF.has(p.role ?? '')) continue;
    const r = results.get(p.id)?.result;
    const ticks = r ? Object.values(r.objectives) : [];
    const row: RosterRow = {
      id: p.id, name: p.name, role: p.role ?? 'visitor',
      done: ticks.filter((t) => t !== null).length, total: ticks.length, ticksOnTask: r?.ticksOnTask ?? 0,
    };
    if (p.hand !== undefined) row.hand = p.hand;
    if (r?.quiz) row.quizBest = r.quiz.best;
    rows.push(row);
  }
  return rows.sort((a, b) => {
    if (a.hand !== undefined || b.hand !== undefined) {
      if (a.hand === undefined) return 1;
      if (b.hand === undefined) return -1;
      if (a.hand !== b.hand) return a.hand - b.hand;
    }
    return a.name.localeCompare(b.name);
  });
}
